/* eslint-disable max-len */
import React from 'react';
import { FormikErrors } from 'formik';
import { AuthMarketing, Button, Checkbox, FieldWrapper, FormError, Input, LayoutSplit } from 'components';
import { LoaderDots } from 'components/Loader';
import { appConfig } from 'config/app';
import { theme } from 'design';
import { AuthColumns, AuthConsentGroup, AuthContainer, AuthContent, AuthContentInner, AuthFieldset, AuthSSOEmailIcon, AuthSSOInputLayout } from '../Auth/styles';

interface IFormikEmailSignUpValues {
  firstName: string;
  lastName: string;
  identity: string;
  password: string;
  termsOfService: boolean;
  privacyPolicy: boolean;
}

interface ISignUpEmailProps {
  authHeader: JSX.Element;
  onSubmit: () => void;
  formId: string;
  errors: FormikErrors<IFormikEmailSignUpValues>;
  validateField: (field: 'firstName' | 'lastName' | 'identity' | 'termsOfService' | 'privacyPolicy') => boolean;
  isLoading: boolean;
  getFieldProps: (fieldName: string) => any;
  setFieldValue: (fieldName: string, value: any) => void;
  isValid: boolean;
  dirty: boolean;
  values: Record<string, any>;
  errorMessages: string[];
  isLoadingGoogleUser?: boolean;
}

const formatError = (error: string) => {
  if (error.toLowerCase().trim() === 'this contact is already in use on another account.') {
    return 'An account with this email address already exists.';
  }
  return error;
};

const SignUpGoogle = ({ authHeader, onSubmit, errors, validateField, isLoading, getFieldProps, values, errorMessages, isValid, dirty, isLoadingGoogleUser = false }: ISignUpEmailProps) => (
  <LayoutSplit
    left={
      <AuthContainer>
        <AuthContent data-cy="create-account-email-page-1">
          {!isLoadingGoogleUser ? (
            <AuthContentInner>
              {authHeader}
              <form onSubmit={onSubmit}>
                <AuthFieldset>
                  <AuthColumns>
                    <FieldWrapper data-cy="first-name-field" label="First Name" message={validateField('firstName') ? errors.firstName : ''} hasError={validateField('firstName')}>
                      <Input data-cy="first-name-input" disabled={isLoading} id="firstName" type="text" placeholder="John" {...getFieldProps('firstName')} />
                    </FieldWrapper>
                    <FieldWrapper data-cy="last-name-field" label="Last Name" message={validateField('lastName') ? errors.lastName : ''} hasError={validateField('lastName')}>
                      <Input data-cy="last-name-input" id="lastName" disabled={isLoading} type="text" placeholder="Doe" {...getFieldProps('lastName')} />
                    </FieldWrapper>
                  </AuthColumns>
                  <FieldWrapper label="Email">
                    <AuthSSOInputLayout>
                      <AuthSSOEmailIcon icon="Google" />

                      <Input id="identity" type="email" placeholder="" readOnly {...getFieldProps('identity')} />
                    </AuthSSOInputLayout>
                  </FieldWrapper>
                </AuthFieldset>

                <AuthConsentGroup>
                  <FieldWrapper data-cy="terms-of-service-field" message={validateField('termsOfService') ? errors.termsOfService : ''} indentMessage hasError={validateField('termsOfService')}>
                    <Checkbox
                      data-cy="terms-of-service-input"
                      label="I agree to receive electronic disclosures and understand that Kickfin will send account notices to the email address provided."
                      size="large"
                      id="termsOfService"
                      checked={values.termsOfService}
                      {...getFieldProps('termsOfService')}
                    />
                  </FieldWrapper>

                  <FieldWrapper data-cy="privacy-policy-field" message={validateField('privacyPolicy') ? errors.privacyPolicy : ''} indentMessage hasError={validateField('privacyPolicy')}>
                    <Checkbox
                      data-cy="privacy-policy-input"
                      label={
                        <span>
                          I have read and agree to{' '}
                          <a target="_blank" rel="noopener noreferrer" href={appConfig.LINKS.TERMS_OF_USE}>
                            Kickfin's User Agreement
                          </a>{' '}
                          and{' '}
                          <a target="_blank" rel="noopener noreferrer" href={appConfig.LINKS.PRIVACY_POLICY}>
                            Privacy Policy.
                          </a>
                        </span>
                      }
                      size="large"
                      id="privacyPolicy"
                      checked={values.privacyPolicy}
                      {...getFieldProps('privacyPolicy')}
                    />
                  </FieldWrapper>
                </AuthConsentGroup>

                {errorMessages &&
                  errorMessages.map((error: string, index: number) => (
                    <FormError data-cy="form-error" key={`error-message-${index}`}>
                      {formatError(error)}
                    </FormError>
                  ))}

                <Button data-cy="submit-create-account" type="submit" label="Sign Up" disabled={isLoading || !(isValid && dirty)} isProcessing={isLoading} />
              </form>
            </AuthContentInner>
          ) : (
            <LoaderDots size="large" color={theme.color.neutral[900]} />
          )}
        </AuthContent>
      </AuthContainer>
    }
    right={<AuthMarketing />}
  />
);

export default SignUpGoogle;
